import * as React from 'react';
import { formatDuration } from '@cfl/shared';

import { Alert, AlertDescription, Skeleton } from '@/components/cargofl-ui';
import { money } from '@/lib/invoice-api';
import { useUtilisation, type UtilisationRow } from '@/lib/team-api';

/**
 * Utilisation: billable time against the time somebody was available to work.
 *
 * Three ratios, not one, because they fail differently and the fix differs. A
 * person at 40% because they recorded nothing is a different problem from one at
 * 40% because the month was internal work, and a single percentage hides which -
 * so the timesheet ratio and the billable share sit beside it.
 *
 * Capacity comes from the contract through `resolveDay`, the same function behind
 * the working-time targets, and stops at today: a month in progress otherwise
 * shows the whole company at a third of target on the 10th.
 */
export function UtilisationTable({ from, to }: { from: string; to: string }) {
  const { data, isPending, isError, error } = useUtilisation(from, to);

  if (isPending) return <Skeleton className="h-72 w-full" />;
  if (isError) {
    return (
      <Alert className="border-destructive/30">
        <AlertDescription className="text-sm">
          {error instanceof Error ? error.message : 'Could not load utilisation.'}
        </AlertDescription>
      </Alert>
    );
  }

  const withTarget = data.rows.filter((r) => r.utilisation !== null);
  if (withTarget.length === 0) {
    return (
      <Alert>
        <AlertDescription className="text-sm">
          Nobody visible to you has a working contract in this period, so there is no capacity
          to measure billable time against.
        </AlertDescription>
      </Alert>
    );
  }

  const running = data.countedTo !== data.to;
  // Almost everything flagged billable means the flag has never been curated.
  const unflagged = data.flags.projectsTotal - data.flags.projectsBillable;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <p className="text-sm">
          Team utilisation{' '}
          <span className="ts-num text-base font-semibold tabular-nums">
            {percent(data.totals.utilisation)}
          </span>{' '}
          <span className="text-muted-foreground">
            — {formatDuration(data.totals.billableSeconds)} billable of{' '}
            {formatDuration(data.totals.capacitySeconds)} available
          </span>
        </p>
        <p className="ts-num text-sm text-muted-foreground tabular-nums">
          {money(data.totals.billableValueMinor, '₹')} billable
        </p>
      </div>

      {/*
        * Utilisation is only ever as good as the billable flag, and that flag
        * defaults to true - so a project nobody has thought about counts as
        * client work. Said here rather than left for somebody to discover after
        * quoting the number, because a figure over 100% is not a good figure, it
        * is a broken denominator or a numerator counting leave.
        */}
      {unflagged <= 2 && (
        <Alert className="border-amber-500/40">
          <AlertDescription className="text-sm leading-relaxed">
            <strong>
              {data.flags.projectsBillable} of {data.flags.projectsTotal} projects are flagged
              billable
            </strong>{' '}
            — so leave, internal work and admin are being counted as client time, and these
            figures read high. Set the billable flag on Administration → Projects for anything
            that is not sold to a client, and this becomes the real number.
          </AlertDescription>
        </Alert>
      )}

      {running && (
        <p className="text-xs text-muted-foreground">
          The period is still running, so capacity is counted to {data.countedTo}. Counting the
          whole period would show everybody short of target simply because it has not happened yet.
        </p>
      )}

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-muted">
              <Th className="text-left">Person</Th>
              <Th title="Billable time ÷ time available to work">Utilisation</Th>
              <Th>Billable</Th>
              <Th>Other</Th>
              <Th title="Everything recorded ÷ time available to work">Timesheet</Th>
              <Th title="Of what they recorded, how much was billable">Billable share</Th>
              <Th>Value</Th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr key={row.id} className="border-t">
                <td className="whitespace-nowrap px-3 py-1.5 font-medium">
                  {row.name}
                  {row.left && <span className="ml-2 text-xs text-muted-foreground">(left)</span>}
                  {!row.hasContract && (
                    <span className="ml-2 text-xs text-muted-foreground">no contract</span>
                  )}
                </td>
                <td className="px-3 py-1.5">
                  <Bar value={row.utilisation} />
                </td>
                <Num>{formatDuration(row.billableSeconds)}</Num>
                <Num muted>{formatDuration(row.nonBillableSeconds)}</Num>
                <Num muted>{percent(row.recordedRatio)}</Num>
                <Num muted>{percent(row.billableShare)}</Num>
                <Num muted>{money(row.billableValueMinor, '₹')}</Num>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-muted-foreground">
        Services firms are usually run at 70–80% utilisation; the band is marked on each row.
        Non-billable is not waste — it is internal work, and the split is the point.
      </p>
    </div>
  );
}

/**
 * The figure and where it sits against the band.
 *
 * A bar rather than a number alone because the question is never the exact
 * percentage, it is whether somebody is near the band, under it, or nowhere near.
 */
function Bar({ value }: { value: UtilisationRow['utilisation'] }) {
  if (value === null) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const pct = Math.min(value, 1.25) * 80; // 125% fills the track
  const tone =
    value >= 0.7 ? 'bg-emerald-500' : value >= 0.5 ? 'bg-amber-500' : 'bg-destructive';

  return (
    <span className="flex items-center gap-2">
      <span className="relative h-2.5 w-28 shrink-0 overflow-hidden rounded-full bg-muted">
        <span className={`absolute inset-y-0 left-0 ${tone}`} style={{ width: `${pct}%` }} />
        {/* The 70% mark, so a bar is read against the target and not just itself. */}
        <span className="absolute inset-y-0 w-px bg-foreground/40" style={{ left: '56%' }} />
      </span>
      <span className="ts-num w-12 shrink-0 text-right font-medium tabular-nums">
        {percent(value)}
      </span>
    </span>
  );
}

function Th({ children, className = '', title }: { children: React.ReactNode; className?: string; title?: string }) {
  return (
    <th
      title={title}
      className={`px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground ${className}`}
    >
      {children}
    </th>
  );
}

function Num({ children, muted = false }: { children: React.ReactNode; muted?: boolean }) {
  return (
    <td
      className={`ts-num whitespace-nowrap px-3 py-1.5 text-right tabular-nums ${
        muted ? 'text-muted-foreground' : 'font-medium'
      }`}
    >
      {children}
    </td>
  );
}

function percent(value: number | null): string {
  return value === null ? '—' : `${Math.round(value * 100)}%`;
}
